import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';
import { store, getAppState } from '../../redux/store';
import { clearPatientAuth } from '../../redux/slices/patientAuthSlice';
import { formatDateToISO, format24HourTo12Hour } from '../../utils/helpers';
import { PORTAL_ROUTES } from '../constants';

// Per-request flags supported by patientApi:
//  - silent: skip the global error toast (caller surfaces the error itself)
//  - skipAuthRedirect: don't redirect on 401 (e.g. the DOB-auth call itself)
declare module 'axios' {
  export interface AxiosRequestConfig {
    silent?: boolean;
    skipAuthRedirect?: boolean;
  }
}

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';
const NETWORK_ERROR_MESSAGE = 'Network error. Please check your connection and try again.';

const rawBaseUrl = process.env.REACT_APP_PORTAL_API_ENDPOINT;
const normalizedBaseUrl = rawBaseUrl ? rawBaseUrl.replace(/\/+$/, '') : '';

const patientApi = axios.create({
  baseURL: normalizedBaseUrl,
});

patientApi.interceptors.request.use((config) => {
  const token = getAppState().patientAuth.patientToken;
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});

// Backend error envelope is `{ errors: "<message>" }` (string) or, less
// commonly, `{ errors: ["<message>", ...] }` (array). Falls back to the
// default message if neither shape is present or the value is empty.
// If there's no response at all (backend down, offline, CORS), it's a network error.
const extractErrorMessage = (error: AxiosError): string => {
  if (!error.response) return NETWORK_ERROR_MESSAGE;
  const errors = (error.response.data as any)?.errors;
  if (typeof errors === 'string' && errors.trim()) return errors.trim();
  if (Array.isArray(errors) && typeof errors[0] === 'string' && errors[0].trim()) {
    return errors[0].trim();
  }
  return DEFAULT_ERROR_MESSAGE;
};

patientApi.interceptors.response.use(
  (response) => {
    // Auto-unwrap top-level JSON:API envelope so callers don't repeat the
    // dance: `{ data: {...} }` -> `{...}`, `{ data: [...] }` -> `[...]`.
    const body = response.data;
    if (
      body &&
      typeof body === 'object' &&
      !Array.isArray(body) &&
      'data' in body &&
      Object.keys(body).length === 1
    ) {
      response.data = (body as any).data;
    }
    return response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;
    const message = extractErrorMessage(error);
    error.message = message;
    const config = (error.config || {}) as AxiosRequestConfig;

    // TODO: Refine 401 handling — currently does a full-page reload via
    // window.location, which kills all in-memory state. Replace with a
    // shared history/navigate so we stay within the SPA on session expiry.
    if (status === 401 && !config.skipAuthRedirect) {
      store.dispatch(clearPatientAuth());
      redirectToDobEntry();
      return Promise.reject(error);
    }

    if (!config.silent) {
      toast.error(message);
    }
    return Promise.reject(error);
  }
);

function redirectToDobEntry() {
  const { resourceType, encodedResourceId } = getAppState().patientAuth;
  if (resourceType && encodedResourceId) {
    window.location.href = `/${resourceType}/${encodedResourceId}`;
  } else {
    window.location.href = PORTAL_ROUTES.ROOT;
  }
}

export interface PatientAuthResponse {
  patientToken: string;
  patientInfo: {
    fullName: string;
    email?: string;
    phoneNumber?: string;
  };
  practiceInfo: {
    name: string;
    logo?: string;
    phoneNumber?: string;
    address?: string;
  };
}

export interface AppointmentProvider {
  name: string;
  role?: string;
}

export type AppointmentRequestAction = 'confirm' | 'reschedule' | 'cancel';
export type AppointmentRequestState = string;

export interface AppointmentRequest {
  id: string;
  actionType: AppointmentRequestAction | string;
  state: AppointmentRequestState;
  createdAt: string; // ISO
  source?: string;
  // Present for reschedule requests — the slot the patient is proposing.
  proposedAt?: string; // ISO
  proposedDuration?: number;
}

export interface Appointment {
  id: string;
  appointmentAt?: string; // raw ISO timestamp from backend — use this for time math
  date: string; // 'YYYY-MM-DD' — derived from appointmentAt for display
  startTime: string; // '10:00 AM'
  endTime: string; // '10:30 AM'
  durationMinutes: number;
  type?: string;
  provider: AppointmentProvider;
  status: string; // raw value from backend (e.g. 'scheduled', 'cancelled')
  confirmed: boolean;
  notes?: string; // patient-supplied note added at scheduling time
  bookedAt?: string; // ISO timestamp of when the appointment was created
  requests: AppointmentRequest[]; // chronological list (oldest → newest)
}

// Format a Date into a 12-hour HH:MM AM/PM string using shared helpers.
const toDisplayTime = (d: Date): string =>
  format24HourTo12Hour(
    `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
  );

// Extract provider name + role from the JSON:API-nested provider payload.
// Backend shape: attrs.provider.data.attributes = { first_name, last_name, role }
const buildProvider = (attrs: any): { name: string; role: string } => {
  const providerAttrs = attrs?.provider?.data?.attributes;
  if (!providerAttrs) return { name: '', role: '' };
  const name = [providerAttrs.first_name, providerAttrs.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  return { name, role: String(providerAttrs.role ?? '') };
};

// Pull AppointmentRequests off the JSON:API-nested collection. Backend shape:
// attrs.appointment_requests.data[i].attributes = { action_type, state, created_at, ... }
// Returned chronologically (oldest → newest) so the UI can render a timeline
// without re-sorting.
const buildRequests = (attrs: any): AppointmentRequest[] => {
  const list: any[] = attrs?.appointment_requests?.data ?? [];
  const mapped = list
    .map((entry): AppointmentRequest | null => {
      const a = entry?.attributes;
      if (!a) return null;
      return {
        id: String(entry.id ?? ''),
        actionType: String(a.action_type ?? ''),
        state: String(a.state ?? ''),
        createdAt: a.created_at ?? '',
        source: a.source ?? undefined,
        proposedAt: a.appointment_at ?? undefined,
        proposedDuration: a.duration != null ? Number(a.duration) : undefined,
      };
    })
    .filter((r): r is AppointmentRequest => r !== null);

  return mapped.sort((a, b) => {
    const ta = new Date(a.createdAt).getTime();
    const tb = new Date(b.createdAt).getTime();
    return (Number.isNaN(ta) ? 0 : ta) - (Number.isNaN(tb) ? 0 : tb);
  });
};

// Trusts `attrs.encoded_id` to be present — the backend must include it.
// Throws loudly if it's missing rather than silently producing a record with
// an empty/unsafe id that would propagate into URLs and action calls.
const mapAppointment = (attrs: any): Appointment => {
  const duration = Number(attrs.duration ?? 0);
  const start = new Date(attrs.appointment_at);
  const hasValidDate = !Number.isNaN(start.getTime());
  const end = hasValidDate ? new Date(start.getTime() + duration * 60 * 1000) : start;
  if (!attrs.encoded_id) {
    throw new Error('Appointment is missing id — cannot safely render or act on it.');
  }
  return {
    id: String(attrs.encoded_id),
    appointmentAt: hasValidDate ? attrs.appointment_at : undefined,
    date: hasValidDate ? formatDateToISO(start) : '',
    startTime: hasValidDate ? toDisplayTime(start) : '',
    endTime: hasValidDate ? toDisplayTime(end) : '',
    durationMinutes: duration,
    type: attrs.type ?? attrs.appointment_type,
    provider: buildProvider(attrs),
    status: String(attrs.status ?? ''),
    confirmed: Boolean(attrs.confirmed),
    notes: attrs.notes ?? undefined,
    bookedAt: attrs.created_at ?? undefined,
    requests: buildRequests(attrs),
  };
};

/**
 * Authenticate a patient using their date of birth for a specific resource.
 * Returns the patient auth token along with patient and practice info.
 *
 * ENDPOINT: POST {base_url}/session/token
 * REQUEST: { resource: { resource_type, resource_id, birth_date } }
 *
 * TODO: Verify the response-shape mapping (attrs.*) below matches actual backend response.
 */
export async function authenticateWithDob(
  resourceType: string,
  encodedResourceId: string,
  dob: string
): Promise<PatientAuthResponse> {
  // silent: the DOB form surfaces this error inline (not via toast).
  // skipAuthRedirect: a 401 here means "wrong DOB", not session expiry.
  const { data } = await patientApi.post(
    '/session/token',
    {
      resource: {
        resource_type: resourceType,
        resource_id: encodedResourceId,
        birth_date: dob,
      },
    },
    { silent: true, skipAuthRedirect: true }
  );

  // /session/token does not use the JSON:API envelope, so the interceptor's
  // auto-unwrap is a no-op for this response.
  const attrs = data ?? {};
  const practiceAttrs = attrs.practice?.data?.attributes ?? {};

  return {
    patientToken: attrs.auth_token,
    patientInfo: {
      fullName: attrs.full_name,
      email: attrs.email,
      phoneNumber: attrs.mobile_phone,
    },
    practiceInfo: {
      name: practiceAttrs.title,
      logo: practiceAttrs.logo,
      phoneNumber: practiceAttrs.phone_number,
      address: practiceAttrs.address,
    },
  };
}

/**
 * Fetch the authenticated patient's appointments (used by the list view).
 *
 * ENDPOINT: GET {base_url}/appointments
 *
 * TODO: Verify the endpoint path.
 */
export async function fetchAppointments(): Promise<Appointment[]> {
  const { data } = await patientApi.get('/appointments');
  // Interceptor already unwrapped { data: [...] } to [...].
  const items: any[] = Array.isArray(data) ? data : [];
  return items.map((item: any) => mapAppointment(item?.attributes ?? item));
}

/**
 * Fetch a single appointment by its id (used by the show page).
 *
 * ENDPOINT: GET {base_url}/appointments/:id
 *
 * TODO: Verify the endpoint path.
 */
export async function fetchAppointment(id: string): Promise<Appointment> {
  const { data } = await patientApi.get(`/appointments/${id}`);
  const attrs = data?.attributes ?? data ?? {};
  return mapAppointment(attrs);
}

/**
 * Submit an AppointmentRequest from the patient (confirm / reschedule / cancel).
 *
 * The backend creates a pending request that staff later approve/deny — the
 * appointment itself is NOT mutated here. Callers should refetch the
 * appointment so the new request appears in `requests[]`.
 *
 * ENDPOINT: POST {base_url}/appointments/:id/:action
 *
 * The action segment matches the action_type ('confirm' for now; reschedule
 * and cancel will follow the same convention).
 */
export async function createAppointmentRequest(
  appointmentId: string,
  actionType: AppointmentRequestAction,
): Promise<string> {
  await patientApi.post(`/appointments/${appointmentId}/${actionType}`);
  return appointmentId;
}

export default patientApi;
